import React, { memo, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useBridgeAPI } from '@digitaz/bridge-sdk-client';
import { Icon } from '../../Icon';
import ArrowLeftIcon from '../../../public/static/images/webview/icon-arrow-left.svg';
import { Bookmark } from '../../Bookmark';
import { BackButton } from '../../BackButton';
import { useScrollState } from '../../../hooks/useScrollState';
import { BackHistoryStorage } from '../../../utils/helpers/BackHistoryStorage';
import { Actions, AppBar, Button, StyledBackButton } from './styled';
import ShareIcon from '../../../public/static/images/item/share.svg';

interface WebViewAdAppBarProps {
  isBookmarked: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
}

export const WebViewAdAppBar: React.FC<WebViewAdAppBarProps> = memo(
  ({ setFavorited, isBookmarked }) => {
    const { scrollY } = useScrollState();
    const router = useRouter();
    const { share } = useBridgeAPI();

    const { id } = router.query;

    useEffect(() => {
      const backHistoryStorage = BackHistoryStorage.getBackHistoryStorage();
      if (!backHistoryStorage) {
        BackHistoryStorage.setBackHistoryStorage();
      }
    }, []);

    const onShareClickHandler = async () => {
      try {
        share({ url: window.location.href } as Parameters<typeof share>[0]);
      } catch (e) {
        console.log('share operation has error: ', (e as Error).message);
      }
    };

    const progress = useMemo(() => {
      const END_POSITION = 180;
      const percentByPosition = 1 - (END_POSITION - scrollY) / END_POSITION;
      return Math.max(0, Math.min(percentByPosition, 1)).toFixed(2);
    }, [scrollY]);

    return (
      <AppBar $scrollProgress={progress}>
        <StyledBackButton>
          <BackButton icon={ArrowLeftIcon} />
        </StyledBackButton>

        <Actions>
          <Button
            role="button"
            tabIndex={-1}
            onKeyDown={onShareClickHandler}
            onClick={onShareClickHandler}
          >
            <Icon
              indent={false}
              IconComponent={ShareIcon}
              width={35}
              height={35}
            />
          </Button>
          <Button>
            <Bookmark
              itemId={(Array.isArray(id) ? id[0] : id) || ''}
              isActive={isBookmarked}
              setFavorited={setFavorited}
              isWebView
            />
          </Button>
        </Actions>
      </AppBar>
    );
  }
);

WebViewAdAppBar.displayName = `WebViewAdAppBar`;
